import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import './EternalCupEvent.css';

const EternalCupEvent = () => {
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info'); // 'info', 'music', 'location'
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    loadEventData();
  }, []);

  const loadEventData = async () => {
    try {
      const docRef = doc(db, 'eternalCupEvent', 'currentEvent');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setEventData(docSnap.data());
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading event data:', error);
      setLoading(false);
    }
  };

  const handleDownloadMusic = (musicUrl, musicTitle) => {
    const link = document.createElement('a');
    link.href = musicUrl;
    link.download = `${musicTitle}.mp3`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="eternal-cup-event-container">
        <div className="eternal-cup-loading">
          <div className="loading-spinner"></div>
          <p>Φόρτωση...</p>
        </div>
      </div>
    );
  }

  if (!eventData) {
    return (
      <div className="eternal-cup-event-container">
        <div className="eternal-cup-empty">
          <h2>🏆 Eternal Cup Event</h2>
          <p>Δεν υπάρχει ενεργό event προς το παρόν.</p>
          <p>Ελέγξτε ξανά σύντομα!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="eternal-cup-event-container">
      {/* Hero Section with Flyer */}
      <div className="eternal-cup-hero">
        {eventData.flyerUrl && (
          <div className="flyer-wrapper">
            {!imageLoaded && (
              <div className="flyer-skeleton">
                <div className="skeleton-shimmer"></div>
                <p>Φόρτωση αφίσας...</p>
              </div>
            )}
            <img 
              src={eventData.flyerUrl} 
              alt="Eternal Cup Event Flyer" 
              className={`event-flyer ${imageLoaded ? 'loaded' : 'loading'}`}
              loading="eager"
              decoding="async"
              fetchpriority="high"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageLoaded(true)}
            />
          </div>
        )}
        <div className="hero-overlay">
          <h1 className="event-title">🏆 ETERNAL CUP</h1>
          <p className="event-subtitle">Dance Competition</p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="eternal-cup-tabs">
        <button
          className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          <span className="tab-icon">📋</span>
          <span className="tab-label">Πληροφορίες</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'music' ? 'active' : ''}`}
          onClick={() => setActiveTab('music')}
        >
          <span className="tab-icon">🎵</span>
          <span className="tab-label">Μουσική Solo</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'location' ? 'active' : ''}`}
          onClick={() => setActiveTab('location')}
        >
          <span className="tab-icon">📍</span>
          <span className="tab-label">Τοποθεσία</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="eternal-cup-content">
        {/* Πληροφορίες Tab */}
        {activeTab === 'info' && (
          <div className="tab-panel info-panel">
            <div className="panel-header">
              <h2>📋 Πληροφορίες Διαγωνισμού</h2>
            </div>
            <div 
              className="info-content"
              dangerouslySetInnerHTML={{ __html: eventData.infoContent || '<p>Δεν υπάρχουν πληροφορίες διαθέσιμες.</p>' }}
            />
          </div>
        )}

        {/* Μουσική Solo Tab */}
        {activeTab === 'music' && (
          <div className="tab-panel music-panel">
            <div className="panel-header">
              <h2>🎵 Μουσική Solo</h2>
              <p className="panel-subtitle">Κατεβάστε ή ακούστε τα κομμάτια για τις solo κατηγορίες</p>
            </div>
            {eventData.musicTracks && eventData.musicTracks.length > 0 ? (
              <div className="music-grid">
                {eventData.musicTracks.map((track, index) => (
                  <div key={index} className="music-card">
                    <div className="music-card-header">
                      <div className="music-icon">🎼</div>
                      <h3 className="music-title">{track.title}</h3>
                    </div>
                    <div className="music-player">
                      <audio 
                        controls 
                        className="audio-player"
                        preload="metadata"
                      >
                        <source src={track.url} type="audio/mpeg" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                    <button
                      className="download-button"
                      onClick={() => handleDownloadMusic(track.url, track.title)}
                    >
                      <span className="download-icon">⬇️</span>
                      <span>Λήψη</span>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>Δεν υπάρχουν διαθέσιμα μουσικά κομμάτια.</p>
              </div>
            )}
          </div>
        )}

        {/* Τοποθεσία Tab */}
        {activeTab === 'location' && (
          <div className="tab-panel location-panel">
            <div className="panel-header">
              <h2>📍 Τοποθεσία</h2>
            </div>
            
            {eventData.locationInfo && (
              <div className="location-info">
                <div 
                  className="location-text"
                  dangerouslySetInnerHTML={{ __html: eventData.locationInfo }}
                />
              </div>
            )}

            {eventData.mapEmbedUrl && (
              <div className="map-container">
                <iframe
                  src={eventData.mapEmbedUrl}
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Event Location Map"
                  className="location-map"
                ></iframe>
              </div>
            )}

            {!eventData.locationInfo && !eventData.mapEmbedUrl && (
              <div className="empty-state">
                <p>Δεν υπάρχουν πληροφορίες τοποθεσίας.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EternalCupEvent;
